import { useDataPolling } from "@/src/useData";
import useEnv from "@/src/useEnv";
import { useRouter } from "next/router";
import { useCallback, useEffect } from "react";
import { toast } from "sonner";

interface AlertCountsResponse {
  open: number;
  resolved: number;
  dismissed: number;
  mostRecentOpenTimestamp: string | null;
}

// Per-environment last seen timestamps to detect new alerts
const lastSeenTimestamps = new Map<string, string | null>();

export function updateLastSeenTimestamp(
  envPubId: string,
  timestamp: string | null
) {
  lastSeenTimestamps.set(envPubId, timestamp);
}

export function useAlertNotifications(enabled: boolean = true) {
  const { selectedEnv } = useEnv();
  const router = useRouter();
  const envPubId = selectedEnv?.pub_id;

  const { data: counts } = useDataPolling<AlertCountsResponse>(() =>
    enabled && envPubId
      ? `/web/environment/${envPubId}/agents/v1/alerts/counts/`
      : null
  );

  const openCount = counts?.open ?? 0;

  const navigateToAlerts = useCallback(() => {
    router.push("/agent-app/alerts?status=open");
  }, [router]);

  // Show toast when new alerts appear
  useEffect(() => {
    if (!counts || !envPubId) return;

    const mostRecentTimestamp = counts.mostRecentOpenTimestamp;
    const lastSeenTimestamp = lastSeenTimestamps.get(envPubId);

    if (lastSeenTimestamp === undefined) {
      // If it's the first load of the environment, just record the timestamp, don't show toast
      lastSeenTimestamps.set(envPubId, mostRecentTimestamp);
      return;
    }

    const hasNewAlert =
      mostRecentTimestamp !== null &&
      (lastSeenTimestamp === null || mostRecentTimestamp > lastSeenTimestamp);

    if (hasNewAlert) {
      toast("New alert", {
        position: "top-right",
        duration: 10_000,
        action: {
          label: "View",
          onClick: navigateToAlerts,
        },
        actionButtonStyle: {
          backgroundColor: "#ef4444",
          color: "white",
        },
      });
    }

    lastSeenTimestamps.set(envPubId, mostRecentTimestamp);
  }, [counts, navigateToAlerts, envPubId]);

  return { openCount };
}
